import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useTranslation } from "react-i18next";

import type { OrderShift } from "~/providers/shift-provider";
import { useMediaQuery } from "~/hooks/use-media-query";
import { useShiftStore } from "~/stores/use-shift-store";
import { useShiftManager } from "../../../managers/use-shift-manager";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../../ui/drawer";
import OrderShiftForm from "./order-shift-form";

export default function OrderShiftDialog() {
  const { t } = useTranslation("common");
  const { deleteOrderShift } = useShiftManager();

  const isMobile = useMediaQuery(768);
  const {
    setOrderShiftFormOpen,
    currentOrderShift,
    orderShiftFormOpen,
    setCurrentOrderShift,
  } = useShiftStore();

  const handleDialog = (open: boolean) => {
    setOrderShiftFormOpen(open);
    if (open) {
      setCurrentOrderShift(undefined);
    }
  };

  const handleDelete = async (shift: OrderShift) => {
    const result = await deleteOrderShift(shift);

    if (result === true) {
      handleDialog(false);
    }
  };

  if (isMobile) {
    return (
      <Drawer open={orderShiftFormOpen} onOpenChange={handleDialog}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>
              {currentOrderShift && (
                <div className="flex flex-row items-center gap-2">
                  <span>{currentOrderShift.type}</span>
                </div>
              )}
              {!currentOrderShift && <>{t("new_shift")}</>}
            </DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[80vh] min-h-[80vh] small-scrollbar overflow-auto flex flex-col">
            <OrderShiftForm
              shift={currentOrderShift}
              onClose={() => handleDialog(false)}
            />
          </div>
          <DrawerFooter>
            <div className="flex flex-row gap-2 justify-between w-full">
              {currentOrderShift && (
                <Button
                  className="w-full"
                  onClick={() => {
                    void handleDelete(currentOrderShift);
                  }}
                  variant="destructive"
                >
                  {t("delete")}
                </Button>
              )}
              <Button
                className="w-full"
                form="shift-form"
                type="submit"
                variant="success"
              >
                {currentOrderShift ? t("update") : t("create")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={orderShiftFormOpen} onOpenChange={handleDialog}>
      <DialogContent className="max-w-4xl w-full min-h-[60vh]">
        <DialogHeader>
          <DialogTitle>
            {currentOrderShift && (
              <div className="flex flex-row items-center gap-2">
                <span>{currentOrderShift.type}</span>
              </div>
            )}
            {!currentOrderShift && <>{t("new_shift")}</>}
          </DialogTitle>
          <VisuallyHidden>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <div className="max-h-[80vh] min-h-[80vh] small-scrollbar overflow-auto flex flex-col">
          <OrderShiftForm
            shift={currentOrderShift}
            onClose={() => handleDialog(false)}
          />
        </div>
        <DialogFooter>
          <div className="flex flex-row gap-2 justify-between w-full">
            {currentOrderShift && (
              <Button
                className="w-full"
                onClick={() => {
                  void handleDelete(currentOrderShift);
                }}
                variant="destructive"
              >
                {t("delete")}
              </Button>
            )}
            <Button
              className="w-full"
              form="shift-form"
              type="submit"
              variant="success"
            >
              {currentOrderShift ? t("update") : t("create")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
